import { required, useTranslate } from "ra-core";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { ReferenceArrayInput } from "@/components/admin/reference-array-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { NumberInput } from "@/components/admin/number-input";
import { DateInput } from "@/components/admin/date-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { SelectInput } from "@/components/admin/select-input";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

import { contactOptionText } from "../misc/ContactOption";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";

export const DealInputs = () => {
  const isMobile = useIsMobile();
  return (
    <div className="flex flex-col gap-10 p-1">
      <DealInfoInputs />
      <DealEnquiryInputs />

      <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
        <DealLinkedToInputs />
        <Separator orientation={isMobile ? "horizontal" : "vertical"} />
        <DealMiscInputs />
      </div>
    </div>
  );
};

const DealInfoInputs = () => {
  return (
    <div className="flex flex-col gap-4 flex-1">
      <TextInput source="name" validate={required()} helperText={false} />
      <TextInput source="description" multiline rows={3} helperText={false} />
    </div>
  );
};

type DealFormValues = {
  received_at?: string | null;
  first_reply_at?: string | null;
  outcome?: string | null;
  stage?: string | null;
};

const OUTCOMES = ["open", "won", "lost", "no-decision"] as const;

export const validateFirstReply = (
  value: string | null | undefined,
  values: DealFormValues,
) => {
  if (!value || !values.received_at) return undefined;
  return new Date(value) < new Date(values.received_at)
    ? "resources.deals.validation.first_reply_before_received"
    : undefined;
};

export const validateOutcomeReason = (
  value: string | null | undefined,
  values: DealFormValues,
) =>
  (values.outcome === "lost" ||
    values.outcome === "no-decision" ||
    values.stage === "lost") &&
  !value?.trim()
    ? "resources.deals.validation.outcome_reason_required"
    : undefined;

/** Turns a cleared datetime field into null instead of throwing on an invalid date. */
export const parseDateTime = (value: string) =>
  value ? new Date(value).toISOString() : null;

/**
 * ISO instant of the current minute, used to prefill Received on a new enquiry.
 * DateTimeInput renders it in the browser's local timezone.
 */
export const getDefaultReceivedAt = (now: Date = new Date()) =>
  new Date(Math.floor(now.getTime() / 60000) * 60000).toISOString();

const DealEnquiryInputs = () => {
  const { dealSources } = useConfigurationContext();
  const translate = useTranslate();
  const outcomeChoices = OUTCOMES.map((value) => ({
    value,
    label: translate(`resources.deals.outcome_choices.${value}`),
  }));
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <SelectInput
        source="source"
        choices={dealSources}
        optionText="label"
        optionValue="value"
        helperText={false}
      />
      <DateTimeInput
        source="received_at"
        parse={parseDateTime}
        validate={required()}
        helperText={false}
      />
      <DateTimeInput
        source="first_reply_at"
        parse={parseDateTime}
        validate={validateFirstReply}
        helperText={false}
      />
      <SelectInput
        source="outcome"
        choices={outcomeChoices}
        optionText="label"
        optionValue="value"
        defaultValue="open"
        helperText={false}
        validate={required()}
      />
      <TextInput
        source="outcome_reason"
        validate={validateOutcomeReason}
        helperText={false}
      />
    </div>
  );
};

const DealLinkedToInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4 flex-1">
      <h6 className="text-lg font-semibold">
        {translate("resources.deals.inputs.linked_to")}
      </h6>
      <ReferenceInput source="company_id" reference="companies">
        <AutocompleteCompanyInput
          label="resources.deals.fields.company_id"
          validate={required()}
          modal
        />
      </ReferenceInput>

      <ReferenceArrayInput source="contact_ids" reference="contacts_summary">
        <AutocompleteArrayInput
          label="resources.deals.fields.contact_ids"
          optionText={contactOptionText}
          helperText={false}
        />
      </ReferenceArrayInput>
    </div>
  );
};

const DealMiscInputs = () => {
  const { dealStages, dealCategories } = useConfigurationContext();
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4 flex-1">
      <h6 className="text-lg font-semibold">
        {translate("resources.deals.field_categories.misc")}
      </h6>

      <SelectInput
        source="category"
        choices={dealCategories}
        optionText="label"
        optionValue="value"
        helperText={false}
      />
      <NumberInput source="amount" helperText={false} />
      <DateInput source="expected_closing_date" helperText={false} />
      <SelectInput
        source="stage"
        choices={dealStages}
        optionText="label"
        optionValue="value"
        defaultValue={dealStages[0]?.value}
        helperText={false}
        validate={required()}
      />
    </div>
  );
};
